import { FadeUp, SectionHeading } from "./Reveal";

const S = "/projects/shaw";

const PhaseLabel = ({ num, title }) => (
  <div className="flex items-center gap-4 mb-6">
    <span className="font-display text-4xl md:text-5xl font-bold text-[#4361C2]">{num}</span>
    <span className="text-xs tracking-[0.3em] uppercase text-ash border-b border-white/15 pb-1">{title}</span>
  </div>
);

const Caption = ({ children }) => (
  <div className="mt-3 text-[10px] tracking-[0.25em] uppercase text-ash">{children}</div>
);

const Img = ({ src, alt, testId, className = "" }) => (
  <img
    src={src}
    alt={alt}
    loading="lazy"
    className={`w-full object-cover border border-white/15 grayscale hover:grayscale-0 transition-all duration-700 ${className}`}
    data-testid={testId}
  />
);

const FACTS = [
  ["Project", "Watha T. Daniel / Shaw Library"],
  ["Location", "Rhode Island Ave NW, Washington DC"],
  ["Role", "Project Architect — Davis Brody Bond"],
  ["Scope", "25,000 SF Public Library"],
];

export const CaseStudyShaw = () => (
  <section id="case-study-shaw" className="px-6 md:px-12 py-28 md:py-40 bg-surface border-y border-white/10" data-testid="shaw-case-study-section">
    <SectionHeading index="Case Study 05 — Public Building" title="Shaw Library" testId="shaw-case-study-heading" />

    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-5">
        <dl className="border border-white/15 divide-y divide-white/10" data-testid="shaw-case-study-facts">
          {FACTS.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-4 px-5 py-4">
              <dt className="text-[10px] tracking-[0.3em] uppercase text-ash shrink-0">{k}</dt>
              <dd className="text-sm font-medium text-right">{v}</dd>
            </div>
          ))}
        </dl>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-6 md:col-start-7">
        <p className="text-lg md:text-xl font-light leading-relaxed text-paper" data-testid="shaw-case-study-intro">
          As Project Architect at Davis Brody Bond, Lonny helped shape this 25,000 sq ft public
          library and its communal spaces — developing and refining the section details, plans, and
          elevations that carried the building through permit.
        </p>
        <p className="mt-5 text-sm leading-relaxed text-ash">
          He redesigned the lower-level communal spaces, created the interior elevations, laid out
          the book return and collections area, revised the stair drawings, and coordinated beam
          sizes with the structural engineer — folding them back into the sections. He also
          investigated the Kalwall system and the weathering of the metal mesh along Rhode Island
          Avenue.
        </p>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="01" title="The Building" />
      </FadeUp>
      <FadeUp className="md:col-span-8">
        <Img src={`${S}/hero-dusk.jpg`} alt="Shaw Library glass facade at dusk along Rhode Island Ave" testId="shaw-hero-image" className="aspect-[16/10]" />
        <Caption>The glass prow over Rhode Island Ave NW — at dusk</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-4 md:mt-24">
        <Img src={`${S}/corner.jpg`} alt="Shaw Library corner elevation with metal mesh skin" testId="shaw-corner-image" className="aspect-[3/4]" />
        <Caption>Metal mesh skin — studied for weathering</Caption>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="02" title="The Drawings" />
      </FadeUp>
      <FadeUp className="md:col-span-8">
        <div className="border border-white/15 overflow-hidden bg-[#0d1530]">
          <img
            src={`${S}/drawings.jpg`}
            alt="Shaw Library permit drawing set — plans, sections, elevations and details"
            loading="lazy"
            className="w-full invert opacity-80 mix-blend-screen"
            data-testid="shaw-drawings-image"
          />
        </div>
        <Caption>The permit set — plans, sections, elevations, details</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-4 flex items-center">
        <blockquote className="border-l-2 border-[#4361C2] pl-6 py-2" data-testid="shaw-case-study-quote">
          <p className="font-display text-xl md:text-2xl uppercase font-bold leading-tight tracking-tight">
            "Beam sizes from the engineer, drawn straight back into the sections."
          </p>
        </blockquote>
      </FadeUp>
    </div>
  </section>
);
